/**
 * Error Fingerprinting for Deduplication
 *
 * Creates unique fingerprints for errors to identify duplicate errors
 * and group similar errors together for better error tracking.
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md Phase 1 - Week 2
 */
import type { ApplicationError } from '@/types/errors';

/**
 * Generate a fingerprint for an error
 *
 * Creates a unique hash based on error properties that identify
 * the same error occurring multiple times.
 *
 * @param error - The error to fingerprint
 * @returns Unique fingerprint string
 *
 * @example
 * ```typescript
 * const error = ErrorFactory.createNetworkError('Connection failed', {
 *   status: 500,
 *   url: '/api/expenses'
 * });
 * const fingerprint = generateErrorFingerprint(error);
 * // fingerprint: "network-500-/api/expenses-Connection failed"
 * ```
 */
export function generateErrorFingerprint(error: unknown): string {
  if (!error) {
    return 'unknown-null';
  }

  // Handle ApplicationError
  if (isApplicationError(error)) {
    const parts: string[] = [error.category];

    // Add error-specific identifiers
    if ('status' in error && error.status) {
      parts.push(`status-${error.status}`);
    }

    if ('url' in error && error.url) {
      // Normalize URL (remove query params and IDs)
      const normalized = normalizeUrl(String(error.url));
      parts.push(`url-${normalized}`);
    }

    if ('code' in error && error.code) {
      parts.push(`code-${error.code}`);
    }

    if ('queryKey' in error && error.queryKey) {
      const queryStr = Array.isArray(error.queryKey)
        ? error.queryKey.join('-')
        : String(error.queryKey);
      parts.push(`query-${queryStr}`);
    }

    // Add message signature (first 50 chars, normalized)
    if (error.message) {
      const msgSignature = normalizeMessage(error.message);
      parts.push(`msg-${msgSignature}`);
    }

    return hashString(parts.join('|'));
  }

  // Handle standard Error
  if (error instanceof Error) {
    const parts: string[] = [
      error.name || 'Error',
      normalizeMessage(error.message),
    ];

    // Include stack signature (first few frames)
    if (error.stack) {
      const stackSignature = getStackSignature(error.stack);
      parts.push(`stack-${stackSignature}`);
    }

    return hashString(parts.join('|'));
  }

  // Handle other error types
  return hashString(`generic-${String(error)}`);
}

/**
 * Type guard for ApplicationError
 */
function isApplicationError(error: unknown): error is ApplicationError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'category' in error &&
    'severity' in error &&
    'timestamp' in error
  );
}

/**
 * Normalize URL by removing dynamic parts
 *
 * @example
 * "/api/expenses/abc123/comments" → "/api/expenses/:id/comments"
 */
function normalizeUrl(url: string): string {
  return url
    .replace(/\/[0-9a-f]{6,}/gi, '/:id') // UUIDs and hex IDs
    .replace(/\/\d+/g, '/:id') // Numeric IDs
    .replace(/\?.*$/, '') // Query params
    .substring(0, 100); // Limit length
}

/**
 * Normalize error message by removing dynamic values
 *
 * @example
 * "User 123 not found" → "User :id not found"
 */
function normalizeMessage(message: string): string {
  if (!message) return '';

  return String(message)
    .replace(/\b[0-9a-f]{6,}\b/gi, ':id') // UUIDs and hex IDs
    .replace(/\b\d+\b/g, ':num') // Numbers
    .replace(/['"][^'"]*['"]/g, ':str') // Quoted strings
    .substring(0, 100); // Limit length
}

/**
 * Extract stack signature (first 3 stack frames, normalized)
 */
function getStackSignature(stack: string): string {
  const frames = stack
    .split('\n')
    .slice(1, 4) // First 3 frames
    .map(frame => {
      // Extract function name and file
      const match = frame.match(/at\s+([^\s]+)\s+\(([^:]+)/);
      if (match) {
        const [, fn, file] = match;
        return `${fn}@${file.split('/').pop()}`;
      }
      return frame.trim().substring(0, 50);
    })
    .join('|');

  return frames || 'no-stack';
}

/**
 * Simple string hashing function
 * Uses FNV-1a algorithm for fast, consistent hashes
 */
function hashString(str: string): string {
  let hash = 2166136261; // FNV offset basis

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619); // FNV prime
  }

  // Convert to positive hex string
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Check if two errors have the same fingerprint
 *
 * @example
 * ```typescript
 * const error1 = new Error('Connection failed');
 * const error2 = new Error('Connection failed');
 *
 * if (isSameError(error1, error2)) {
 *   console.log('Duplicate error detected');
 * }
 * ```
 */
export function isSameError(error1: unknown, error2: unknown): boolean {
  const fp1 = generateErrorFingerprint(error1);
  const fp2 = generateErrorFingerprint(error2);
  return fp1 === fp2;
}

/**
 * Error deduplicator using fingerprints
 *
 * Tracks seen errors and their counts within a time window.
 */
export class ErrorDeduplicator {
  private seen: Map<
    string,
    { count: number; firstSeen: number; lastSeen: number }
  > = new Map();
  private windowMs: number;

  constructor(windowMs = 60 * 1000) {
    this.windowMs = windowMs;
  }

  /**
   * Check if an error should be reported
   *
   * Returns false if the error was seen recently (within window).
   * Updates seen count and timestamp.
   *
   * @returns True if error should be reported, false if it's a duplicate
   */
  shouldReport(error: unknown): boolean {
    const fingerprint = generateErrorFingerprint(error);
    const now = Date.now();
    const seen = this.seen.get(fingerprint);

    if (!seen) {
      // First time seeing this error
      this.seen.set(fingerprint, {
        count: 1,
        firstSeen: now,
        lastSeen: now,
      });
      return true;
    }

    // Update count and timestamp
    seen.count++;
    seen.lastSeen = now;

    // Check if outside window (report again)
    if (now - seen.firstSeen > this.windowMs) {
      // Reset for new window
      seen.count = 1;
      seen.firstSeen = now;
      return true;
    }

    // Duplicate within window
    return false;
  }

  /**
   * Get stats for a specific error fingerprint
   */
  getStats(
    error: unknown
  ): { count: number; firstSeen: number; lastSeen: number } | null {
    const fingerprint = generateErrorFingerprint(error);
    return this.seen.get(fingerprint) || null;
  }

  /**
   * Clear deduplication cache
   */
  clear(): void {
    this.seen.clear();
  }
}

/**
 * Global error deduplicator
 * 1 minute window for deduplication
 */
export const globalDeduplicator = new ErrorDeduplicator(60 * 1000);
